'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { useCallback, useEffect, useState } from 'react'
import { getCampaignViewer, type CampaignViewer } from '@kiao-voice/api-client'
import { readStoredToken } from '@/features/auth/auth-storage'
import { createWebAuthedNoContextClient, getWebAuthedNoContextClient } from '@/lib/web-api-client'

/**
 * `expired` has no server state of its own — it is `owned` whose access end has
 * passed. Deriving it here keeps one date on the wire instead of a second state
 * that could disagree with it, and「還剩 0 天」is simply the wrong message for
 * someone already back on Free.
 */
export type CampaignViewerView =
  | { kind: 'unknown' }
  | { kind: 'guest' }
  | { kind: 'eligible' }
  | { kind: 'owned'; planName: string; accessEndsAt: string }
  | { kind: 'expired'; planName: string; accessEndsAt: string | null }
  | { kind: 'subscribed'; subscription: NonNullable<CampaignViewer['subscription']> }

function derive(viewer: CampaignViewer, now: Date): CampaignViewerView {
  if (viewer.state === 'subscribed' && viewer.subscription) {
    return { kind: 'subscribed', subscription: viewer.subscription }
  }
  if (viewer.state === 'owned') {
    const planName = viewer.planName ?? ''
    const endsAt = viewer.accessEndsAt
    const stillRunning = endsAt !== null && new Date(endsAt).getTime() > now.getTime()
    return stillRunning
      ? { kind: 'owned', planName, accessEndsAt: endsAt! }
      : { kind: 'expired', planName, accessEndsAt: endsAt }
  }
  return { kind: 'eligible' }
}

/**
 * Who is looking at this campaign, resolved per campaign rather than per user:
 * "already claimed" is a fact about this campaign, not about the account.
 *
 * A failed lookup degrades to `guest`: the claim endpoint is the authority, and
 * the worst case is one extra OTP the server then refuses.
 */
export function useCampaignViewer(
  slug: string,
  deps: {
    /** Skip the lookup entirely — for hosts that already know the viewer. */
    enabled?: boolean
    readToken?: typeof readStoredToken
    fetchFn?: typeof fetch
  } = {}
): CampaignViewerView {
  const { enabled = true, readToken = readStoredToken, fetchFn } = deps
  const [viewer, setViewer] = useState<CampaignViewerView>({ kind: 'unknown' })

  const resolve = useCallback(async (): Promise<CampaignViewerView> => {
    if (!readToken()) return { kind: 'guest' }
    try {
      const client = fetchFn
        ? createWebAuthedNoContextClient(fetchFn)
        : getWebAuthedNoContextClient()
      const body = await getCampaignViewer(client, slug)
      return derive(body.viewer, new Date())
    } catch (caughtError) {
      reportHandledException(caughtError, {
        source: 'features/promotion-campaign/use-campaign-viewer.ts',
      })
      // Degrades to `guest`: the claim endpoint is the authority, and the worst
      // case is one extra OTP the server then refuses. Hosts that must not show
      // a claim entry to a subscriber gate on `unknown` instead of trusting
      // this fallback.
      return { kind: 'guest' }
    }
  }, [fetchFn, readToken, slug])

  useEffect(() => {
    if (!enabled) return
    let active = true
    void resolve().then((next) => {
      if (active) setViewer(next)
    })
    return () => {
      active = false
    }
  }, [enabled, resolve])

  return viewer
}
